import React, { SyntheticEvent, useEffect, useState } from 'react'
import { Flex } from '@chakra-ui/react'
import { css } from '@emotion/react'
import bytes from 'bytes'
import { t, Trans } from '@lingui/macro'

import Form, { FormRow } from 'front-end/src/components/common/form/Form'
import Input from 'front-end/src/components/common/form/input/Input'
import Label from 'front-end/src/components/common/form/label/Label'
import { Upload } from 'front-end/src/components/common/form/upload/Upload'
import {
  ButtonClean,
  ButtonDanger,
  ButtonPrimary
} from 'front-end/src/components'
import { Modal } from '../../../components/common/modal/Modal'
import {
  LooopS3BucketEnum,
  WorkspaceBookmarkModelInput
} from '../../../graphql/private/PrivateSchemaTypes'
import { WorkspaceBookmarksQuery } from './WorkspaceBookmarksTab.codegen'
import { TypeOfElement } from '../../../../../types/generic'
import {
  useCreateBookmarkMutation,
  usePatchBookmarkMutation
} from './BookmarkFormModal.codegen'
import { LooopS3FileUpload } from '../../../app/util/LooopS3FileUpload'
import { notification } from '../../../components/common/notifications/Notifications'

const MAX_FILE_SIZE = '5MB'

interface IBookmarkFormkModalProps {
  workspaceId: number
  modalVisible: boolean
  hideModal: (boolean) => void
  onBookmarkChange: () => void
  bookmarkToEdit: TypeOfElement<
    NonNullable<WorkspaceBookmarksQuery['workspace']>['bookmarkList']
  > | null
  showImageUpload: boolean
}

export const BookmarkFormModal = ({
  workspaceId,
  bookmarkToEdit,
  modalVisible,
  hideModal,
  onBookmarkChange,
  showImageUpload
}: IBookmarkFormkModalProps) => {
  const [isSaving, setIsSaving] = useState(false)
  const [title, setTitle] = useState('')
  const [url, setUrl] = useState('')
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null)
  const [coverImageBase64, setCoverImageBase64] = useState<
    string | ArrayBuffer | null
  >(null)

  const [createBookmark] = useCreateBookmarkMutation()
  const [patchBookmark] = usePatchBookmarkMutation()

  useEffect(() => {
    if (bookmarkToEdit) {
      setTitle(bookmarkToEdit.title)
      setUrl(bookmarkToEdit.url)
    }
  }, [bookmarkToEdit])

  const onImageChange = (e: SyntheticEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files
    if (files?.length) {
      const file = files[0]
      if (file.type.match('image.*')) {
        setCoverImageFile(file)

        const reader = new FileReader()
        reader.onload = () => {
          setCoverImageBase64(reader.result as string)
        }
        reader.readAsDataURL(file)
      }
    }
  }

  const isSaveButtonEnabled = () => {
    if (!bookmarkToEdit) {
      return true
    }
    if (
      bookmarkToEdit.title !== title ||
      bookmarkToEdit.url !== url ||
      coverImageFile
    ) {
      return true
    }
    return false
  }

  return (
    <Modal
      header={t`${bookmarkToEdit ? 'Edit' : 'Create new'} bookmark`}
      show={modalVisible}
      controls={[
        <ButtonClean
          disabled={isSaving}
          onClick={() => hideModal(false)}
          key={1}
        >
          <Trans>Cancel</Trans>
        </ButtonClean>,
        <ButtonPrimary
          isLoading={isSaving}
          key={2}
          disabled={!isSaveButtonEnabled()}
          onClickAsync={async () => {
            if (isSaving) return

            setIsSaving(true)
            let imageCdnUrl
            if (coverImageFile) {
              if (coverImageFile.size > bytes(MAX_FILE_SIZE)) {
                notification.error(
                  t`File is too big. Maximum file size is ${MAX_FILE_SIZE}`
                )

                setIsSaving(false)
                return
              }
              imageCdnUrl = await LooopS3FileUpload({
                S3UploadProps: {
                  filename: coverImageFile.name,
                  S3path: `workspace-bookmark-images/workspace-${workspaceId}`,
                  bucket: LooopS3BucketEnum.PRIVATE
                },
                blob: coverImageFile
              })
            }

            const variables = {
              workspaceId: workspaceId,
              input: {
                title,
                url
              }
            }

            if (imageCdnUrl) {
              ;(variables.input as WorkspaceBookmarkModelInput).image =
                imageCdnUrl
            }

            try {
              if (bookmarkToEdit) {
                await patchBookmark({
                  variables: { ...variables, bookmarkId: bookmarkToEdit.id }
                })
              } else {
                await createBookmark({
                  variables
                })
              }
            } catch (e) {
              setIsSaving(false)
              throw e
            }

            setIsSaving(false)
            onBookmarkChange()
            hideModal(false)
          }}
        >
          {bookmarkToEdit ? t`Update` : t`Create`}
        </ButtonPrimary>
      ]}
      onHide={() => hideModal(false)}
    >
      <Form>
        <FormRow>
          <Label htmlFor={'bookmark_title'}>
            <Trans>Title text</Trans>
          </Label>
          <Input
            id={'bookmark_title'}
            fullWidth
            placeholder={t`Text`}
            maxLength={80}
            value={title}
            onChange={(val) => {
              setTitle(val)
            }}
          />
        </FormRow>
        <FormRow>
          <Label htmlFor={'bookmark_url'}>
            <Trans>Link</Trans>
          </Label>
          <Input
            id={'bookmark_url'}
            fullWidth
            value={url}
            placeholder={t`URL address`}
            onChange={(val) => {
              setUrl(val)
            }}
          />
        </FormRow>
        {showImageUpload && (
          <FormRow>
            <Label htmlFor={'bookmark_image'}>
              <Trans>Background image</Trans> (<Trans>optional</Trans>)
            </Label>

            {!coverImageBase64 && bookmarkToEdit?.image?.original && (
              <>
                <Flex
                  m="12px 0"
                  css={css`
                    img {
                      width: 100%;
                    }
                  `}
                >
                  <img
                    src={bookmarkToEdit.image?.original}
                    alt={t`Cover image`}
                  />
                </Flex>
                <ButtonDanger
                  css={css`
                    margin-bottom: 8px;
                  `}
                  onClick={async () => {
                    setIsSaving(true)
                    await patchBookmark({
                      variables: {
                        workspaceId: workspaceId,
                        bookmarkId: bookmarkToEdit.id,
                        input: {
                          image: null
                        }
                      }
                    })

                    onBookmarkChange()
                    setIsSaving(false)
                  }}
                >
                  <Trans>Remove image</Trans>
                </ButtonDanger>
              </>
            )}
            {coverImageBase64 && (
              <Flex
                m="12px 0"
                css={css`
                  img {
                    width: 100%;
                  }
                `}
              >
                {/* @ts-expect-error  9/11/2020*/}
                <img src={coverImageBase64} alt={t`Cover image`} />
              </Flex>
            )}
            <Upload
              id={'bookmark_image'}
              title={t`Choose image`}
              accept="image/png, image/jpeg"
              onChange={onImageChange}
            />
          </FormRow>
        )}
      </Form>
    </Modal>
  )
}
